import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Dialog, DialogProps, ElevatedButton, Switch } from '@producer/ui-kit';

import { ShareLink } from '../../../app/entities/share-link';
import { createShareLink } from '../share.slice';
import { ShareableLink } from './shareable-link';
import { ContactsSelector } from './contacts-selector';
import { Space } from '../../../app/entities/space';
import { toastPool } from '../../toasts/models/toast-pool';
import { Toast } from '../../toasts/models/toast';
import { InteractableTextInput } from '../../../components/interactable-text-input/interactable-text-input';
import { Error } from '../../../components/inputs/styled-input';

import type { SelectOption } from '../../../components/select';
import { catchError } from '../../../core/catch-error';
import { Asset } from '../../../app/entities/asset';
import { Note } from '../../../components/note/note';

import styles from './share-modal.module.scss';
type ShareModalProps = DialogProps & {
  spaceId: string;
  projectId: string;
  elementIds: string[];
  title?: string;
  onCancel: () => void;
};

export const ShareModal: React.FC<ShareModalProps> = observer(
  ({ spaceId, projectId, title, elementIds, onCancel }) => {
    const { t } = useTranslation('common');

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [linkId, setLinkId] = useState<string>();
    const [recipients, setRecipients] = useState<SelectOption[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>();

    const contacts = Space.getOne(spaceId)?.contacts ?? [];
    const link = linkId ? ShareLink.getOne(linkId) : undefined;

    const options = contacts
      ?.filter((contact) => contact.email)
      ?.map((contact) => {
        const { firstName, lastName, email } = contact;

        return {
          value: email,
          label: firstName || lastName ? `${firstName} ${lastName}` : email,
        };
      });

    useEffect(() => {
      if (!errorMessage) {
        return;
      }

      setTimeout(() => {
        setErrorMessage('');
      }, 3100);
    }, [errorMessage]);

    useEffect(() => {
      const assetIds = elementIds
        .map((id) => Asset.getOne(id))
        .filter((asset): asset is Asset => !!asset && asset.type !== 'folder')
        .map((asset) => asset._id);

      createShareLink({ projectId, assetIds, spaceId }).then((link) => {
        setLinkId(link._id);
      });
      // TODO For some reason depending on elementIds causes infinite loop
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, spaceId]);

    const hasFolders = useMemo(
      () => elementIds.some((id) => Asset.getOne(id)?.type === 'folder'),
      [elementIds],
    );

    const handleSendInvites = async () => {
      setIsLoading(true);
      const emails = recipients.map((recipient) => recipient.value);

      await link!
        .update({ recipients: emails })
        .then(() => {
          toastPool.insert(
            new Toast(`Share link sent to ${t('personWithCount', { count: emails.length })}.`),
          );
          setIsLoading(false);
          onCancel();
        })
        .catch((e) => {
          catchError(e);
          // TODO: Display error message when error model is ready instead of hard coded message
          setIsLoading(false);
        });
    };

    const handleOnClose = () => {
      onCancel();
    };

    const handleNameChange = async (value: string) => {
      if (!value) return;

      await link!.update({ name: value });
    };

    if (!link) {
      return null;
    }

    const shareTitle =
      title ||
      (elementIds?.length && `Sharing ${t('itemWithCount', { count: elementIds.length })}`);

    return (
      <Dialog
        title={shareTitle}
        onClose={handleOnClose}
        actions={
          <ElevatedButton
            key="Share"
            text="Share"
            disabled={!recipients.length}
            onClick={handleSendInvites}
            isLoading={isLoading}
          />
        }
      >
        <div className={styles['container']}>
          {hasFolders && (
            <Note isFullWidth>
              Folders won't be part of the share link. This feature will follow soon.
            </Note>
          )}
          <div className={styles['content']}>
            <InteractableTextInput
              label="Link name"
              helpText="This is used internally for you to be able to identify this link from others."
              defaultValue={link.name}
              onSave={handleNameChange}
            />

            <div className={styles['download-settings']}>
              <p>Allow people to download the files</p>

              <Switch
                onToggle={(canDownload) => link.update({ canDownload })}
                isToggled={link.settings?.canDownload}
                size="medium"
              />
            </div>
          </div>

          {link && (
            <div className={styles['content']}>
              <div className={styles['input-container']}>
                <p>Share via link</p>

                <ShareableLink url={link.url} />
              </div>

              <div className={styles['input-container']}>
                <p>Share via email</p>

                <ContactsSelector
                  contacts={options}
                  recipients={recipients}
                  setRecipients={setRecipients}
                  isLoading={false}
                  onError={setErrorMessage}
                />

                {errorMessage && <Error className={styles['inline-error']}>{errorMessage}</Error>}
              </div>
            </div>
          )}
        </div>
      </Dialog>
    );
  },
);
